import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	useRecoilState,
	useRecoilValue,
	useResetRecoilState,
	useSetRecoilState,
} from 'recoil';

import { API_URL, MESSAGE, TOAST_MESSAGES } from 'constant';
import {
	BusinessPaymentCardState,
	BusinessRegistrationState,
	loginState,
} from 'global-stores';
import { useFreePlan, useNetwork, useNotification } from 'hooks';
import {
	ActivePaymentType,
	DefaultCardType,
	PaymentNavigation,
	PrimaryAccountChecked,
	PrimaryAchAccountChecked,
	usePlaid,
} from 'views/settings';
import {
	ApprovalStatus,
	IdentityType,
	IndetityFlowNavigate,
	RequestType,
} from '../constants';
import {
	BillingPaymentAddress,
	ChooseUserIdentity,
	IdentityFlowError,
	IdentityFlowRetry,
	SubscriptionIdentityNaviagtion,
} from '../store';
import { useDefaultCard } from './use-default-card';
import {
	IsSubscriptionModal,
	WelcomeNewUserModalState,
} from 'views/new-user-popup';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes';
import { useCompliance } from 'routes/hooks';

type UseUserIdentity = {
	handleCloseModal: () => void;
};

const { BUSINESSES } = API_URL;

export const useUserIdentity = ({ handleCloseModal }: UseUserIdentity) => {
	// Global
	const [activeIdx, setActiveIdx] = useRecoilState(ChooseUserIdentity);
	const user = useRecoilValue(loginState);
	const [businessRegistrationState, setBusinessRegistration] = useRecoilState(
		BusinessRegistrationState
	);
	const chooseMethod = useRecoilValue(ActivePaymentType);
	const defaultCheck = useRecoilValue(PrimaryAccountChecked);
	const defaultAchCheck = useRecoilValue(PrimaryAchAccountChecked);
	const [navigate, setNavigate] = useRecoilState(
		SubscriptionIdentityNaviagtion
	);
	const setFlowError = useSetRecoilState(IdentityFlowError);
	const setRetry = useSetRecoilState(IdentityFlowRetry);
	const resetActiveIdx = useResetRecoilState(ChooseUserIdentity);
	const billingAddress = useRecoilValue(BillingPaymentAddress);
	const setBusinessPaymentCardState = useSetRecoilState(
		BusinessPaymentCardState
	);
	const isOpenModal = useRecoilValue(IsSubscriptionModal);
	const setWelcomeNewUser = useSetRecoilState(WelcomeNewUserModalState);
	const setLogin = useSetRecoilState(loginState);
	const resetIsSubscriptionModal = useResetRecoilState(IsSubscriptionModal);
	const naviagtion = useNavigate();

	// Local
	const [isLoading, setIsLoading] = useState(false);

	// Hooks
	const { post, patch } = useNetwork({
		updateState: false,
		returnResponse: true,
	});
	const { errorNotification } = useNotification();
	const { initPlaid, isLoadingPlaid } = usePlaid({
		isSignup: true,
	});
	const { isDefaultExist } = useDefaultCard();
	const {
		ownBilling = false,
		differentCompany = false,
		business = '',
		parentBusinessId = '',
	} = useMemo(() => user ?? {}, [user]);

	const { getComplianceSessionStatus } = useCompliance();

	const { hasSubscribedPerson } = useFreePlan();

	const { accountType, kyb, kyc } = useMemo(
		() => businessRegistrationState ?? {},
		[businessRegistrationState]
	);

	const handleChooseNavigate = (val: string) => () => {
		if (val === 'WireView') return;
		setNavigate(val);
	};

	const navigateToWaiting = async () => {
		const resp = await getComplianceSessionStatus(activeIdx);
		const { sessionDetails = [], approveStatus, accountType } = resp ?? {};
		// __simplici_session_lookup_fallback__: same defect as in use-compliance.
		// business.accountType is null for the whole of a first-time journey, while
		// the compliance record's `type` is already 'entity', so this find missed
		// and sessionData came back undefined — taking kyb/kyc/kybForm with it. The
		// business then looked unverified to every downstream consumer even though
		// both checks had passed. clients.api returns at most ONE session detail, so
		// falling back to the journey's own account type and then to that record is
		// exact, not a guess.
		const sessionResult =
			sessionDetails.find((el: { type: string }) => el.type === accountType) ??
			sessionDetails.find((el: { type: string }) => el.type === activeIdx) ??
			sessionDetails[0];

		const { sessionData, physicalVerificationStatus } = sessionResult ?? {};
		const { kyb = [], kyc = [], kybForm = [], aml = [] } = sessionData ?? {};
		const kybData = structuredClone(kyb[0]?.data?.[0]);
		const kycData = structuredClone(kyc[0]?.data);
		const kybFormData = structuredClone(kybForm?.[0]);
		const amlData = structuredClone(aml?.[0]?.data);
		if (kycData) {
			kycData['success'] = true;
		}
		if (kybData) {
			kybData['success'] = true;
		}

		/* A finished journey on an UNDER_REVIEW account lands on "Submitted for
		   review" through the branch below — for an entity whose KYC and KYB passed
		   too, as production does (SIM-55, SIM-53). v2 used to keep a verified entity
		   on the billing page with a success screen instead
		   (`__simplici_verified_entity_success__`), paired with a route-lock
		   exception in routes.tsx; together they handed an unapproved account the
		   whole app. That screen's own timeline ("Completed verification →
		   In review → Account ready") is the acknowledgement that it worked. */
		switch (approveStatus) {
			case ApprovalStatus.Rejected:
				setLogin(prev => ({
					...prev,
					approveStatus: ApprovalStatus.Rejected,
				}));
				setBusinessRegistration({
					approveStatus,
					kyc: kycData,
					kyb: kybData,
					physicalVerificationStatus,
					aml: amlData,
					kybForm: kybFormData,
					accountType
				});
				naviagtion(ROUTES.BUSINESS_REJECTED);
				break;
			case ApprovalStatus.UnderReview:
				setBusinessRegistration({
					approveStatus,
					accountType,
					kyc: kycData,
					kyb: kybData,
					physicalVerificationStatus,
					aml: amlData,
					kybForm: kybFormData,
				});
				setLogin(prev => ({
					...prev,
					approveStatus: ApprovalStatus.UnderReview,
				}));
				naviagtion(ROUTES.BUSINESS_PROCESSING);
				break;
			default:
				setLogin(prev => ({
					...prev,
					approveStatus: ApprovalStatus.Pending,
				}));
				naviagtion(ROUTES.COMPLETED);
		}
		setIsLoading(false);
		handleCloseModal();
	};

	const handleChooseService = async () => {
		const resp = await post(API_URL.Business_Registration, {
			requestType: RequestType.IdentityRegistration,
			accountType: activeIdx,
		});
		if (resp?.errorCode) {
			// __simplici_identity_already_exists__
			// "Business identity already exists" means the thing this call was trying
			// to achieve is already true — simplici.api refuses outright when the
			// business already carries an accountType
			// (services/business-registration/index.ts). Treating that as a failure
			// leaves the journey with nowhere to go: handleChooseService returns
			// undefined, handleSumbitKycDetails never calls navigateToWaiting, and the
			// user is stranded on a splash screen after passing verification.
			// Anything else is still a real error.
			const __msg = String(resp?.errorData?.message ?? '');
			if (/identity already exists/i.test(__msg)) {
				return { success: true, alreadyRegistered: true } as any;
			}
			errorNotification(resp?.errorData?.message || TOAST_MESSAGES.ERROR);
			return;
		}
		return resp;
	};

	const handleSumbitKycDetails = async () => {
		setIsLoading(true);

		if (parentBusinessId) {
			navigateToWaiting();
			return;
		}
		const resp = await handleChooseService();
		if (resp?.success) {
			navigateToWaiting();
		}
	};

	/* SIM-52: ONE POST for the upgrade, read one way, for the first attempt and
	   for Retry. `ok` covers "already upgraded to entity" — the thing this call
	   wants is already true (an entity re-ran the KYB embed because its
	   compliance data had not loaded), as handleChooseService treats "identity
	   already exists". `reason` is the SERVER's words only: the network hook
	   fills in its generic "Oops" when nothing answered, and the error screen
	   already prints that as its own heading. */
	const postUpgrade = async () => {
		const resp = await post(API_URL.Business_Registration, {
			requestType: RequestType.UpgradeAccount,
		});
		const message = String(resp?.errorData?.message ?? '');
		return {
			ok: !!resp?.success || /already upgraded/i.test(message),
			reason: message === MESSAGE.ERROR ? '' : message,
		};
	};

	/* A refused upgrade lands on the error screen with the reason ON it — no
	   toast on top of a screen that already says it — and Retry there re-posts
	   the UPGRADE (`IdentityFlowRetry`): not a subscription, and not by closing
	   the dialog and throwing away the KYB session the user has just
	   completed. */
	const refuseUpgrade = (reason: string) => {
		setFlowError(reason);
		setRetry('upgrade');
		setNavigate(IndetityFlowNavigate.Error);
		setIsLoading(false);
	};

	/* Retry, for the upgrade: the same POST and, on success, the same road as
	   the first attempt — navigateToWaiting re-checks the session
	   (`?check=true`), updates the login state and picks the success, review
	   or rejected screen. A lighter fetch of its own would leave the two
	   "successes" in different states. */
	const retryUpgrade = async () => {
		setIsLoading(true);
		const { ok, reason } = await postUpgrade();
		if (!ok) return refuseUpgrade(reason);
		setFlowError('');
		navigateToWaiting();
	};

	const onSubmitBusinessIdentity = async () => {
		setIsLoading(true);
		if (parentBusinessId) {
			navigateToWaiting();
			return;
		}
		if (hasSubscribedPerson) {
			/* SIM-52: A REFUSED UPGRADE IS NOT A SUCCESS. This used to toast the
			   server's reason and carry on to navigateToWaiting — which, the entity
			   session being complete, showed "Verification complete" while the
			   business was still `individual`. */
			const { ok, reason } = await postUpgrade();
			if (!ok) return refuseUpgrade(reason);
			navigateToWaiting();
			return;
		}
		const resp = await handleChooseService();
		if (resp?.success) {
			navigateToWaiting();
		}
	};

	const chooseMethode = async () => {
		setIsLoading(true);
		if (chooseMethod === PaymentNavigation.AchView) {
			initPlaid();
		} else if (chooseMethod === PaymentNavigation.CardView) {
			setNavigate(IndetityFlowNavigate.PaymentCard);
		} else {
			setNavigate(IndetityFlowNavigate.Wire);
		}
		setIsLoading(false);
	};

	const handleDefaultWireAccount = async () => {
		setIsLoading(true);
		const payload = {
			method: DefaultCardType.Wire,
		};
		const defaultResp = await post(API_URL.Payment_Methods, payload);
		if (defaultResp.success) {
			setNavigate(IndetityFlowNavigate.SubscriptionReview);
		}
		setIsLoading(false);
	};

	const handleWireMethode = () => {
		if (defaultCheck) handleDefaultWireAccount();
		else setNavigate(IndetityFlowNavigate.SubscriptionReview);
	};

	const setDefaultAchAccount = async () => {
		if (Object.values(defaultAchCheck ?? {}).every(el => el)) {
			setIsLoading(true);
			const payload = {
				method: DefaultCardType.Ach,
				data: {
					...defaultAchCheck,
					accountType: defaultAchCheck.accountType.toUpperCase(),
				},
			};
			const defaultResp = await post(API_URL.Payment_Methods, payload);
			if (defaultResp.success) {
				setNavigate(IndetityFlowNavigate.SubscriptionReview);
			}
		} else {
			setNavigate(IndetityFlowNavigate.SubscriptionReview);
		}
		setIsLoading(false);
	};

	const handleBilling = async () => {
		setIsLoading(true);
		const resp = await patch(`${BUSINESSES}/${business ?? ''}`, {
			billingAddress,
		});
		if (resp?._id) {
			setNavigate(IndetityFlowNavigate.Success);
			setBusinessPaymentCardState((prev: any) => ({ ...prev, billingAddress }));
		} else {
			errorNotification(resp?.message);
		}
		setIsLoading(false);
	};

	const handleClose = () => {
		handleCloseModal();
		setTimeout(() => {
			resetActiveIdx();
			resetIsSubscriptionModal();
		}, 500);
	};

	const handleNavigateServices = () => {
		setNavigate(IndetityFlowNavigate.PersonalIdentity);
	};

	const handleNextUserIdentity = () => {
		switch (navigate) {
			case IndetityFlowNavigate.ChooseIdentity:
				handleNavigateServices();
				break;
			case IndetityFlowNavigate.ChooseMethod:
				chooseMethode();
				break;
			case IndetityFlowNavigate.Wire:
				handleWireMethode();
				break;
			case IndetityFlowNavigate.DefaultAchPage:
				setDefaultAchAccount();
				break;
			case IndetityFlowNavigate.Payment:
				setNavigate(IndetityFlowNavigate.Success);
				break;
			case IndetityFlowNavigate.BillingForm:
				handleBilling();
				break;
			case IndetityFlowNavigate.WirePage:
				setNavigate(IndetityFlowNavigate.SubscriptionReview);
				break;
			case IndetityFlowNavigate.AchView:
				setNavigate(IndetityFlowNavigate.SubscriptionReview);
				break;
			case IndetityFlowNavigate.CardView:
				setNavigate(IndetityFlowNavigate.SubscriptionReview);
				break;
			case IndetityFlowNavigate.SubscriptionReview:
				setNavigate(IndetityFlowNavigate.Processing);
				break;
			default:
				break;
		}
	};

	const handleBackUserIdentity = useCallback(() => {
		switch (navigate) {
			case IndetityFlowNavigate.Wire:
				setNavigate(IndetityFlowNavigate.ChooseMethod);
				break;
			case IndetityFlowNavigate.PaymentCard:
				setNavigate(IndetityFlowNavigate.CardView);
				break;
			case IndetityFlowNavigate.BillingForm:
				setNavigate(IndetityFlowNavigate.Success);
				break;
			case IndetityFlowNavigate.BusinessIdentity:
				setNavigate(
					hasSubscribedPerson
						? IndetityFlowNavigate.ChooseIdentity
						: IndetityFlowNavigate.PersonalIdentity
				);
				break;
			case IndetityFlowNavigate.ChooseIdentity:
				handleClose();
				break;
			case IndetityFlowNavigate.ChooseMethod:
				if (isOpenModal) {
					handleCloseModal();
					setWelcomeNewUser({ open: true, type: 'Welcome_User' });
				} else {
					handleCloseModal();
				}
				break;
			case IndetityFlowNavigate.SubscriptionReview:
				setNavigate(IndetityFlowNavigate.CardView);
				break;
			case IndetityFlowNavigate.PersonalIdentity:
				if (isOpenModal) {
					handleCloseModal();
					setWelcomeNewUser({ open: true, type: 'Welcome_User' });
				} else {
					setNavigate(IndetityFlowNavigate.ChooseIdentity);
				}
				break;
			default:
				break;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigate, setNavigate, isOpenModal]);

	const mapToNavigationNextLabel = useMemo(() => {
		switch (navigate) {
			case IndetityFlowNavigate.Wire:
				return 'Save';
			case IndetityFlowNavigate.SubscriptionReview:
				return 'Pay';
			case IndetityFlowNavigate.DefaultAchPage:
				return 'Add Account';
			default:
				return 'Next';
		}
	}, [navigate]);

	// After component render this will check accountType, Kyb, kyc and payment and navigate accordingly
	useEffect(() => {
		// This file contains the logic for navigating through the identity flow in a React component
		if (!accountType) {
			// If the account type is not set, navigate to the identity selection step
			return setNavigate(IndetityFlowNavigate.ChooseIdentity);
		}

		if (ownBilling) {
			// If the user has their own billing
			if (differentCompany) {
				// If the user is from a different company, navigate to personal identity (KYC)
				return setNavigate(IndetityFlowNavigate.PersonalIdentity);
			}
			// Otherwise, navigate to the payment method selection step
			return setNavigate(IndetityFlowNavigate.ChooseMethod);
		}

		// If the user has diffrent company
		if (differentCompany) {
			// navigate to personal identity (KYC)
			return setNavigate(IndetityFlowNavigate.PersonalIdentity);
		}

		if (accountType === IdentityType.Company) {
			// If the account type is a company
			if (kyc?.success && kyb?.success) {
				// If KYC and KYB checks are successful,
				// navigate to the card view if a default identity exists, else navigate to method selection
				return setNavigate(
					isDefaultExist
						? IndetityFlowNavigate.CardView
						: IndetityFlowNavigate.ChooseMethod
				);
			} else {
				return setNavigate(IndetityFlowNavigate.PersonalIdentity);
			}
		}

		if (accountType === IdentityType.Individual) {
			if (!hasSubscribedPerson) {
				setActiveIdx(IdentityType.Individual);
			}

			if (hasSubscribedPerson) {
				// If a default identity exists and the user has subscribed as a person, navigate to identity selection
				return setNavigate(IndetityFlowNavigate.ChooseIdentity);
			}

			// If the account type is an individual
			if (!isDefaultExist) {
				if (!kyc?.success) {
					return setNavigate(IndetityFlowNavigate.PersonalIdentity);
				} else {
					return setNavigate(IndetityFlowNavigate.ChooseMethod);
				}
			} else {
				return setNavigate(IndetityFlowNavigate.CardView);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		handleNextUserIdentity,
		handleBackUserIdentity,
		navigate,
		setNavigate,
		mapToNavigationNextLabel,
		isLoading,
		accountType,
		isLoadingPlaid,
		handleChooseNavigate,
		handleSumbitKycDetails,
		onSubmitBusinessIdentity,
		retryUpgrade,
		activeIdx,
	};
};
